import { useCallback, useMemo, useState } from 'react';
import { useSetHeaderBack, useSetHeaderTitle } from 'shared/hooks/useSetHeader';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'shared/constants/ROUTES';
import { EditProfileContainer } from './EditProfile.styles';
import { EditProfilePhotoEditorProvider } from './context/EditProfilePhotoEditorV2Provider';
import { RepresentativesModalProvider } from 'pages/Representatives/context/RepresentativesModalProvider';
import { useEditProfileData } from './hooks/useEditProfileData';
import { SnackbarProvider } from 'notistack';
import { EditProfileMediaProvider } from './context/EditProfileMediaProvider';
import EditProfileSideInfo from './components/EditProfileSideInfo/EditProfileSideInfo';
import EditProfileSkeletons from './components/EditProfileSkeletons/EditProfileSkeletons';
import EditProfileMedia from './components/EditProfileMedia/EditProfileMedia';
import RepresentativeErrorUI from 'pages/Representatives/components/RepresentativeErrorUI/RepresentativeErrorUI';

const EditProfile = () => {
  useSetHeaderBack(true);
  useSetHeaderTitle('Edit Representatives');
  const {
    data,
    isLoading,
    isFetching,
    isError,
    isOpen,
    sideInfoId,
    handleAction,
    refetch,
    NotificationModal,
    isPermissionGranted,
  } = useEditProfileData();
  const [isCompositeCard, setIsCompositeCard] = useState<boolean>(false);
  const MemoizedNotificationModal = useMemo(() => <NotificationModal />, [isOpen]);
  const history = useHistory();

  const handleIsCompositeCard = useCallback((value: boolean) => setIsCompositeCard(value), []);

  if (isError || data?.errors || data?.data?.profile?.archived)
    return (
      <RepresentativeErrorUI
        message={isError ? undefined : 'This profile is not available for now'}
        onButtonClick={() => (isError ? refetch() : history.push(ROUTES.TALENT.REPRESENTATIVES))}
        buttonText={isError ? 'Try again' : 'Go Back'}
      />
    );

  if (isLoading || (isFetching && !data)) return <EditProfileSkeletons />;

  return (
    <RepresentativesModalProvider>
      <SnackbarProvider autoHideDuration={3000}>
        <EditProfilePhotoEditorProvider>
          {data?.data ? (
            <EditProfileContainer style={{ ...(isCompositeCard && { display: 'grid' }) }}>
              {isOpen ? MemoizedNotificationModal : null}
              {isCompositeCard ? null : <EditProfileSideInfo data={data.data?.profile} handleAction={handleAction} />}
              <EditProfileMediaProvider handleAction={handleAction} isPermissionGranted={isPermissionGranted}>
                <EditProfileMedia handleIsCompositeCard={handleIsCompositeCard} />
              </EditProfileMediaProvider>
            </EditProfileContainer>
          ) : null}
        </EditProfilePhotoEditorProvider>
      </SnackbarProvider>
    </RepresentativesModalProvider>
  );
};

export default EditProfile;
